import React, { useEffect, useState } from "react";
import API from "../services/api";
import "./PageBuilder.css";

import CreateTagSectionForm from "../components/pagebuilder/CreateTagSectionForm";
import CreatePageForm from "../components/pagebuilder/CreatePageForm";
import AddSectionToPageForm from "../components/pagebuilder/AddSectionToPageForm";
import AddTagToSectionForm from "../components/pagebuilder/AddTagToSectionForm";
import ExistingPagesList from "../components/pagebuilder/ExistingPagesList";
import ExistingSectionsList from "../components/pagebuilder/ExistingSectionsList";

const PageBuilder = () => {
  // Lists
  const [pages, setPages] = useState([]);
  const [sections, setSections] = useState([]);
  const [tags, setTags] = useState([]);

  // Create Section
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionDesc, setSectionDesc] = useState("");
  const [sectionMode, setSectionMode] = useState("analog"); // analog | digital | io

  // Create Page
  const [pageTitle, setPageTitle] = useState("");
  const [pageDesc, setPageDesc] = useState("");

  // Link section -> page
  const [selectedPageId, setSelectedPageId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [pageSectionOrder, setPageSectionOrder] = useState(0);

  // Link tag -> section
  const [selectedSectionIdForTag, setSelectedSectionIdForTag] = useState("");
  const [selectedTagId, setSelectedTagId] = useState("");
  const [sectionTagOrder, setSectionTagOrder] = useState(0);

  const [message, setMessage] = useState("");

  const fetchAll = async () => {
    try {
      const [pRes, sRes, tRes] = await Promise.all([
        API.get("/pages/"),
        API.get("/tag-sections/"),
        API.get("/tags/"),
      ]);
      setPages(pRes.data || []);
      setSections(sRes.data || []);
      setTags(tRes.data || []);
    } catch (e) {
      console.error(e);
      setMessage("Failed to load data from API.");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Create Section
  const createSection = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      await API.post("/tag-sections/", {
        title: sectionTitle,
        description: sectionDesc,
        mode: sectionMode,
      });
      setSectionTitle("");
      setSectionDesc("");
      setSectionMode("analog");
      setMessage("Section created.");
      fetchAll();
    } catch (e) {
      console.error(e);
      setMessage("Failed to create section.");
    }
  };

  // Create Page
  const createPage = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      await API.post("/pages/", {
        title: pageTitle,
        description: pageDesc,
      });
      setPageTitle("");
      setPageDesc("");
      setMessage("Page created.");
      fetchAll();
    } catch (e) {
      console.error(e);
      setMessage("Failed to create page.");
    }
  };

  // Add Section to Page
  const addSectionToPage = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!selectedPageId || !selectedSectionId) {
      setMessage("Please select a page and a section.");
      return;
    }

    try {
      await API.post("/page-sections/", {
        page: Number(selectedPageId),
        section: Number(selectedSectionId),
        order: Number(pageSectionOrder) || 0,
      });
      setMessage("Section added to page.");
      fetchAll();
    } catch (e) {
      console.error(e);
      setMessage("Failed to add section to page (maybe duplicate?).");
    }
  };

  // Add Tag to Section
  const addTagToSection = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!selectedSectionIdForTag || !selectedTagId) {
      setMessage("Please select a section and a tag.");
      return;
    }

    try {
      await API.post("/tag-section-tags/", {
        section: Number(selectedSectionIdForTag),
        tag: Number(selectedTagId),
        order: Number(sectionTagOrder) || 0,
      });
      setMessage("Tag added to section.");
      fetchAll();
    } catch (e) {
      console.error(e);
      setMessage("Failed to add tag to section (maybe duplicate?).");
    }
  };

  return (
    <div className="pb-container">
      <h1 className="pb-title">Page Builder</h1>
      <p className="pb-subtitle">
        Create Pages, Sections (Analog/Digital/IO) and attach sections to pages.
      </p>

      {message && <div className="pb-message">{message}</div>}

      <div className="pb-grid">
        <CreateTagSectionForm
          sectionTitle={sectionTitle}
          setSectionTitle={setSectionTitle}
          sectionDesc={sectionDesc}
          setSectionDesc={setSectionDesc}
          sectionMode={sectionMode}
          setSectionMode={setSectionMode}
          onSubmit={createSection}
        />

        <CreatePageForm
          pageTitle={pageTitle}
          setPageTitle={setPageTitle}
          pageDesc={pageDesc}
          setPageDesc={setPageDesc}
          onSubmit={createPage}
        />

        <AddSectionToPageForm
          pages={pages}
          sections={sections}
          selectedPageId={selectedPageId}
          setSelectedPageId={setSelectedPageId}
          selectedSectionId={selectedSectionId}
          setSelectedSectionId={setSelectedSectionId}
          pageSectionOrder={pageSectionOrder}
          setPageSectionOrder={setPageSectionOrder}
          onSubmit={addSectionToPage}
        />
      </div>

      <AddTagToSectionForm
        sections={sections}
        tags={tags}
        selectedSectionIdForTag={selectedSectionIdForTag}
        setSelectedSectionIdForTag={setSelectedSectionIdForTag}
        selectedTagId={selectedTagId}
        setSelectedTagId={setSelectedTagId}
        sectionTagOrder={sectionTagOrder}
        setSectionTagOrder={setSectionTagOrder}
        onSubmit={addTagToSection}
      />

      <div className="pb-lists">
        <ExistingSectionsList sections={sections} />
        <ExistingPagesList pages={pages} setMessage={setMessage} />
      </div>
    </div>
  );
};

export default PageBuilder;